import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarCheck, CheckCircle2, XCircle, ArrowRight, Download, Upload } from "lucide-react";
import { toast } from "sonner";

export default function LeaveAdmin() {
  const qc = useQueryClient();
  const { data: types } = useQuery({ queryKey: ["leaveTypes"], queryFn: async () => (await api.get("/leave-types")).data });
  const { data: reqs, isLoading } = useQuery({ queryKey: ["leaveReqs"], queryFn: async () => (await api.get("/leave-requests", { params: { status: "Pending_HR" } })).data });
  const { data: compOffReqs, isLoading: compOffLoading } = useQuery({ queryKey: ["compOffReqs"], queryFn: async () => (await api.get("/attendance/admin/comp-off-requests", { params: { status: "Pending_HR" } })).data });
  const { data: cascadeConfig } = useQuery({ queryKey: ["cascadeConfig"], queryFn: async () => (await api.get("/leave-cascade-config")).data });
  const { data: cascadeLog, isLoading: cascadeLogLoading } = useQuery({ queryKey: ["cascadeLog"], queryFn: async () => (await api.get("/leave-cascade-log")).data });
  const [cascadeEnabled, setCascadeEnabled] = useState(true);
  useEffect(() => { if (cascadeConfig) setCascadeEnabled(cascadeConfig.enabled); }, [cascadeConfig]);

  const updateCascade = useMutation({
    mutationFn: async (enabled) => (await api.put("/leave-cascade-config", { enabled, order: cascadeConfig?.order || ["CL", "PL"] })).data,
    onSuccess: () => { toast.success("Cascade setting updated"); qc.invalidateQueries({ queryKey: ["cascadeConfig"] }); },
    onError: (e) => { toast.error(apiError(e.response?.data?.detail)); setCascadeEnabled(cascadeConfig?.enabled ?? true); },
  });

  const [migrationAsOf, setMigrationAsOf] = useState("2026-09-30");
  const [migrationResult, setMigrationResult] = useState(null);
  const migrationFileRef = useRef(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await api.get("/leave-opening-balances/bulk-template", { params: { as_of: migrationAsOf }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `opening_balances_template_${migrationAsOf}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download the template");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const bulkImport = useMutation({
    mutationFn: async () => {
      const file = migrationFileRef.current?.files?.[0];
      if (!file) throw new Error("no-file");
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/leave-opening-balances/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      setMigrationResult(data);
      if (data.errors.length === 0) toast.success(`${data.applied} opening balance(s) set`);
      else toast.warning(`${data.applied} set, ${data.errors.length} row(s) had issues — see details below`);
      if (migrationFileRef.current) migrationFileRef.current.value = "";
    },
    onError: (e) => {
      if (e.message === "no-file") toast.error("Choose a CSV file first");
      else toast.error(apiError(e.response?.data?.detail));
    },
  });

  const seed = useMutation({
    mutationFn: async () => (await api.post("/leave-types/seed-standard")).data,
    onSuccess: (d) => { toast.success(`Loaded ${d.added} standard leave types`); qc.invalidateQueries({ queryKey: ["leaveTypes"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decide = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/leave-requests/${id}/decide`, { action, remarks: action === "approve" ? "Approved" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["leaveReqs"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const decideCompOff = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/attendance/admin/comp-off-requests/${id}/decide`, { action, remarks: action === "approve" ? "Approved" : "Rejected" })).data,
    onSuccess: () => { toast.success("Decision recorded"); qc.invalidateQueries({ queryKey: ["compOffReqs"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Time Off" title="Leave Administration" icon={CalendarCheck}
        subtitle="Configure leave types (per Service Manual Section 6) and give final approval. Requests with a Reporting Officer are recommended by them first (comp-off is decided outright, in one step). To carry an employee's balance over from the paper register (or correct it), open that employee's profile → Leave tab." />
      <Tabs defaultValue="requests">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="requests" data-testid="tab-leave-requests">Pending Requests ({reqs?.length || 0})</TabsTrigger>
          <TabsTrigger value="comp-off" data-testid="tab-comp-off-requests">Comp-Off Requests ({compOffReqs?.length || 0})</TabsTrigger>
          <TabsTrigger value="types" data-testid="tab-leave-types">Leave Types ({types?.length || 0})</TabsTrigger>
          <TabsTrigger value="cascade" data-testid="tab-leave-cascade">Cascade Rules</TabsTrigger>
          <TabsTrigger value="migration" data-testid="tab-leave-migration">Opening Balances</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-6">
          {isLoading ? <Loading /> : (reqs || []).length === 0 ? (
            <EmptyState icon={CalendarCheck} title="No pending leave requests" hint="Employee leave requests awaiting approval appear here." />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Type</TableHead>
                  <TableHead className="overline text-slate-500">From → To</TableHead>
                  <TableHead className="overline text-slate-500">Days</TableHead>
                  <TableHead className="overline text-slate-500">Reason</TableHead>
                  <TableHead className="overline text-slate-500">RO Recommendation</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reqs.map((r) => (
                    <TableRow key={r.id} className="table-row-i" data-testid={`leave-pending-${r.id}`}>
                      <TableCell className="font-medium text-navy">{r.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{r.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600">{r.leave_type_code}{r.auto_cascaded && <span className="block text-[10px] text-amber-600">cascaded</span>}</TableCell>
                      <TableCell className="text-slate-600">{r.from_date} → {r.to_date}</TableCell>
                      <TableCell className="text-slate-600">{r.days}{r.half_day ? " (½)" : ""}{r.medical_cert_required ? " ⚕" : ""}</TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px]"><span className="block whitespace-normal break-words">{r.reason}</span>{r.backup_name && <span className="block text-[10px] text-teal">Backup: {r.backup_name} ({r.backup_status})</span>}</TableCell>
                      <TableCell className="text-slate-500 text-xs max-w-[160px]">{r.ro_decided_by ? <>Recommended by {r.ro_decided_by}{r.ro_remarks ? ` — ${r.ro_remarks}` : ""}</> : <span className="text-slate-300">No reporting officer in chain</span>}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decide.mutate({ id: r.id, action: "approve" })} data-testid={`leave-approve-${r.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decide.mutate({ id: r.id, action: "reject" })} data-testid={`leave-reject-${r.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="comp-off" className="mt-6">
          {compOffLoading ? <Loading /> : (compOffReqs || []).length === 0 ? (
            <EmptyState icon={CalendarCheck} title="No pending comp-off requests" hint="Requests to work on a weekly off / holiday, and cover-duty comp-offs, from employees without a reporting officer appear here." />
          ) : (
            <Card className="border-slate-200 shadow-none overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead className="overline text-slate-500">Employee</TableHead>
                  <TableHead className="overline text-slate-500">Date</TableHead>
                  <TableHead className="overline text-slate-500">Hours</TableHead>
                  <TableHead className="overline text-slate-500">Reason</TableHead>
                  <TableHead className="text-right overline text-slate-500">Decision</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {compOffReqs.map((c) => (
                    <TableRow key={c.id} className="table-row-i" data-testid={`comp-off-pending-${c.id}`}>
                      <TableCell className="font-medium text-navy">{c.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{c.employee_code}</span></TableCell>
                      <TableCell className="text-slate-600 font-mono-i text-sm">
                        {c.work_date}
                        {(() => { const d = new Date(c.work_date + "T00:00:00"); const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); return d.getDate() === lastDay; })() && (
                          <span className="block text-[10px] text-amber-600 font-sans">Month-end — payout if unused</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">{c.source === "work_request" ? <span className="text-xs">Advance request<span className="block text-[10px] text-slate-400">approve = permission to work; credited after 6+ hours</span></span> : `${c.hours_worked}h`}</TableCell>
                      <TableCell className="text-slate-500 text-xs min-w-[200px] max-w-[340px] whitespace-normal break-words">{c.reason}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decideCompOff.mutate({ id: c.id, action: "approve" })} data-testid={`comp-off-approve-${c.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => decideCompOff.mutate({ id: c.id, action: "reject" })} data-testid={`comp-off-reject-${c.id}`}><XCircle className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="types" className="mt-6 space-y-3">
          <div className="flex items-center justify-between rounded-md border border-dashed border-slate-200 bg-white px-4 py-3">
            <span className="text-sm text-slate-500">{(types || []).length === 0 ? "No leave types configured. Load the standard set from the Service Manual." : "Re-run to pull in any newly added standard leave types (idempotent)."}</span>
            <Button className="bg-navy" onClick={() => seed.mutate()} data-testid="seed-leave-types">Load Standard Set</Button>
          </div>
          <Card className="border-slate-200 shadow-none overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Code</TableHead>
                <TableHead className="overline text-slate-500">Name</TableHead>
                <TableHead className="overline text-slate-500">Annual</TableHead>
                <TableHead className="overline text-slate-500">Accrual/mo</TableHead>
                <TableHead className="overline text-slate-500">Applies To</TableHead>
                <TableHead className="overline text-slate-500">Encash</TableHead>
                <TableHead className="overline text-slate-500">Ref</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(types || []).map((t) => (
                  <TableRow key={t.id} className="table-row-i" data-testid={`ltype-${t.code}`}>
                    <TableCell className="font-mono-i text-sm text-teal">{t.code}</TableCell>
                    <TableCell className="font-medium text-navy">{t.name}{t.lwp ? " (LWP)" : ""}</TableCell>
                    <TableCell className="text-slate-600">{t.annual_entitlement || "—"}</TableCell>
                    <TableCell className="text-slate-600">{t.monthly_accrual || (t.fixed ? "fixed" : "—")}</TableCell>
                    <TableCell className="text-slate-500 text-xs">{(t.applies_to || []).join(", ") || "All"}{t.gender ? ` · ${t.gender}` : ""}</TableCell>
                    <TableCell>{t.encashable ? <StatusBadge status="Active" /> : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{t.source_ref}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="cascade" className="mt-6 space-y-6">
          <Card className="border-slate-200 shadow-none p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-serif-display text-lg text-navy">Cascade Priority</h4>
                <p className="text-sm text-slate-500 mt-1 max-w-xl">
                  When a half-day (from an attendance grace/late incident, or a leave application that
                  exceeds balance) can't be fully covered by the requested type, it rolls forward through
                  this order. Leave Without Pay is always the final, unconfigurable step.
                </p>
                <p className="text-xs text-slate-500 mt-2 max-w-xl">
                  Leave applications never roll into Paid Leave (PL cannot be combined with another leave
                  category): a CL application short of balance goes straight to LWP. This order applies in
                  full only to attendance half-days.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0" data-testid="cascade-enabled-toggle">
                <span className="text-sm text-slate-500">{cascadeEnabled ? "Enabled" : "Disabled"}</span>
                <Switch checked={cascadeEnabled} onCheckedChange={(v) => { setCascadeEnabled(v); updateCascade.mutate(v); }} />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {(cascadeConfig?.order || ["CL", "PL"]).map((code, i, arr) => (
                <span key={code} className="flex items-center gap-2">
                  <span className="rounded-md border border-teal/30 bg-teal/5 text-teal px-3 py-1.5 text-sm font-medium">
                    {types?.find((t) => t.code === code)?.name || code}
                  </span>
                  {(i < arr.length - 1 || cascadeEnabled) && <ArrowRight className="h-4 w-4 text-slate-300" />}
                  {i === arr.length - 1 && !cascadeEnabled && <span className="text-xs text-slate-400">(bridge disabled — falls straight to LWP)</span>}
                </span>
              ))}
              <span className="rounded-md border border-slate-200 bg-slate-50 text-slate-500 px-3 py-1.5 text-sm font-medium">
                {types?.find((t) => t.lwp)?.name || "Leave Without Pay"}
              </span>
            </div>
            {!cascadeEnabled && (
              <p className="text-xs text-amber-600 mt-3">
                With cascading disabled, only the first type ({types?.find((t) => t.code === (cascadeConfig?.order || [])[0])?.name || (cascadeConfig?.order || [])[0]}) is tried before falling straight to LWP.
              </p>
            )}
          </Card>

          <div>
            <h4 className="font-serif-display text-lg text-navy mb-3">Cascade Deduction Log</h4>
            {cascadeLogLoading ? <Loading /> : (cascadeLog || []).length === 0 ? (
              <EmptyState icon={CalendarCheck} title="No cascade deductions yet" hint="Auto-deductions from attendance half-days and leave applications appear here." />
            ) : (
              <Card className="border-slate-200 shadow-none overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead className="overline text-slate-500">Employee</TableHead>
                    <TableHead className="overline text-slate-500">Trigger</TableHead>
                    <TableHead className="overline text-slate-500">Requested</TableHead>
                    <TableHead className="overline text-slate-500">Breakdown</TableHead>
                    <TableHead className="overline text-slate-500">Cascaded?</TableHead>
                    <TableHead className="overline text-slate-500">When</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {cascadeLog.map((c) => (
                      <TableRow key={c.id} className="table-row-i" data-testid={`cascade-log-${c.id}`}>
                        <TableCell className="font-medium text-navy">{c.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{c.employee_code}</span></TableCell>
                        <TableCell className="text-slate-600 text-xs">{c.trigger === "attendance_half_day" ? "Attendance Half Day" : "Leave Application"}</TableCell>
                        <TableCell className="text-slate-600">{c.requested_code}</TableCell>
                        <TableCell className="text-slate-600 text-xs">{c.breakdown.map((b) => `${b.days}d ${b.leave_type_code}`).join(" + ")}</TableCell>
                        <TableCell>{c.cascaded ? <span className="text-amber-600 text-xs font-medium">Yes</span> : <span className="text-slate-300 text-xs">No</span>}</TableCell>
                        <TableCell className="text-slate-400 text-xs">{(c.created_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="migration" className="mt-6 space-y-6">
          <Card className="border-slate-200 shadow-none p-5">
            <h4 className="font-serif-display text-lg text-navy">Opening Balance Migration</h4>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              For staff already working before the new leave engine took over: enter each person's actual
              CL / PL balance as of a cutover date, and the system calculates everything from there forward
              under the new rules — it never stacks a fresh year-to-date accrual on top of a stale figure.
              Download the template below (pre-filled with every current employee), fill in the balances,
              and upload it back here. A row with a blank balance is skipped, not zeroed out.
            </p>
            <div className="flex items-end gap-3 mt-4 flex-wrap">
              <div>
                <Label className="text-xs text-slate-500">Balances as of</Label>
                <Input type="date" value={migrationAsOf} onChange={(e) => setMigrationAsOf(e.target.value)}
                      className="mt-1 w-44" data-testid="migration-as-of" />
              </div>
              <Button variant="outline" onClick={downloadTemplate} disabled={downloadingTemplate} data-testid="migration-download-template">
                <Download className="h-4 w-4 mr-1.5" /> {downloadingTemplate ? "Preparing…" : "Download Template"}
              </Button>
            </div>
          </Card>

          <Card className="border-slate-200 shadow-none p-5">
            <h4 className="font-serif-display text-lg text-navy mb-3">Upload Filled Template</h4>
            <div className="flex items-center gap-3 flex-wrap">
              <input ref={migrationFileRef} type="file" accept=".csv" className="text-sm" data-testid="migration-file-input" />
              <Button className="bg-navy" onClick={() => bulkImport.mutate()} disabled={bulkImport.isPending} data-testid="migration-upload-submit">
                <Upload className="h-4 w-4 mr-1.5" /> {bulkImport.isPending ? "Uploading…" : "Upload & Apply"}
              </Button>
            </div>
            {migrationResult && (
              <div className="mt-4 text-sm" data-testid="migration-result">
                <p className="text-navy">
                  <span className="font-semibold text-emerald-600">{migrationResult.applied} applied</span>
                  {" · "}<span className="text-slate-400">{migrationResult.skipped_blank} blank (skipped)</span>
                  {migrationResult.errors.length > 0 && <>{" · "}<span className="text-red-500 font-semibold">{migrationResult.errors.length} error(s)</span></>}
                </p>
                {migrationResult.errors.length > 0 && (
                  <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {migrationResult.errors.map((err, i) => (
                      <li key={i} className="text-xs text-red-500">{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
